import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  Box, Chip, Typography, Avatar, Button,
} from '@mui/material'
import {
  Search as SearchIcon,
  VerifiedUser as VerifyIcon,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../components/shared/PageHeader'
import { ListToolbar } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { DataTable, type Column } from '../components/ui/DataTable'
import { themeColors } from '../app/layout/theme'
import { customerVerificationApi } from '../lib/api/customerVerificationApi'
import type { CustomerVerificationRecord, CustomerVerificationStatus } from '../types/verificationTypes'

// ── Status Config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<CustomerVerificationStatus, { bg: string; color: string; border: string }> = {
  'Pending':             { bg: '#FFF8E1', color: '#F57F17', border: '#FFD54F' },
  'In Review':          { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
  'Approved':           { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
  'Rejected':           { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
  'Need More Documents':{ bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
}

function StatusBadge({ status }: { status: CustomerVerificationStatus }) {
  const cfg = STATUS_CONFIG[status] ?? { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' }
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        height: 22,
        borderRadius: '6px',
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        fontSize: '11px',
        fontWeight: 600,
        '& .MuiChip-label': { px: '8px' },
        whiteSpace: 'nowrap',
      }}
    />
  )
}

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}
const AVATAR_COLORS = ['#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f']
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CustomerVerificationList() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('crm_customer_verif_page_size')
    return saved ? Number(saved) : 10
  })

  const verifiers = customerVerificationApi.getVerifiers()
  const statuses = customerVerificationApi.getStatuses()
  const types = customerVerificationApi.getTypes()

  const filterDefs: FilterDef[] = useMemo(() => [
    {
      type: 'checkbox',
      key: 'status',
      label: 'Status',
      options: statuses.map(s => ({ label: s, value: s })),
    },
    {
      type: 'checkbox',
      key: 'verifier',
      label: 'Verifier',
      options: verifiers.map(v => ({ label: v, value: v })),
    },
    {
      type: 'checkbox',
      key: 'type',
      label: 'Verification Type',
      options: types.map(t => ({ label: t, value: t })),
    },
  ], [statuses, verifiers, types])

  const params = useMemo(() => ({
    search: search || undefined,
    status: filters.checkboxes.status?.length ? (filters.checkboxes.status as CustomerVerificationStatus[]) : undefined,
    assignedVerifier: filters.checkboxes.verifier?.length ? filters.checkboxes.verifier : undefined,
    verificationType: filters.checkboxes.type?.length ? (filters.checkboxes.type as any[]) : undefined,
    page,
    pageSize,
  }), [search, filters, page, pageSize])

  const { data, isLoading } = useQuery({
    queryKey: ['customer-verifications', params],
    queryFn: () => customerVerificationApi.getAll(params),
    staleTime: 30_000,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const hasFilters = countActiveFilters(filters) > 0 || !!search

  function clearFilters() {
    setSearch(''); setFilters(EMPTY_UNIFIED_FILTERS); setPage(1)
  }

  const columns: Column[] = [
    {
      id: 'customerId',
      label: 'Customer ID',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.link, fontWeight: 600, fontFamily: 'monospace' }}>
          {row.customerId}
        </Typography>
      ),
    },
    {
      id: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28, fontSize: '10px', fontWeight: 700, bgcolor: avatarColor(row.customerName) }}>
            {initials(row.customerName)}
          </Avatar>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
            {row.customerName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'companyName',
      label: 'Company Name',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{row.companyName}</Typography>
      ),
    },
    {
      id: 'mobileNumber',
      label: 'Mobile Number',
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.primary }}>{row.mobileNumber}</Typography>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>
          {row.email}
        </Typography>
      ),
    },
    {
      id: 'verificationType',
      label: 'Verification Type',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Chip
          label={row.verificationType}
          size="small"
          sx={{ height: 20, borderRadius: '4px', bgcolor: '#EDE7FF', color: themeColors.primary, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '8px' } }}
        />
      ),
    },
    {
      id: 'assignedVerifier',
      label: 'Assigned Verifier',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Avatar sx={{ width: 22, height: 22, fontSize: '9px', fontWeight: 700, bgcolor: avatarColor(row.assignedVerifier) }}>
            {initials(row.assignedVerifier)}
          </Avatar>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{row.assignedVerifier}</Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => <StatusBadge status={row.status} />,
    },
    {
      id: 'submittedDate',
      label: 'Submitted Date',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{formatDate(row.submittedDate)}</Typography>
      ),
    },
    {
      id: 'lastUpdated',
      label: 'Last Updated',
      sortable: true,
      render: (_, row: CustomerVerificationRecord) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.muted }}>{formatDate(row.lastUpdated)}</Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (_, row: CustomerVerificationRecord) => (
        <Button
          size="small"
          variant="outlined"
          onClick={(e) => { e.stopPropagation(); navigate(`/customer-verification/${row.id}`) }}
          sx={{ fontSize: '11px', height: 26, borderRadius: '6px', textTransform: 'none', borderColor: themeColors.border, color: themeColors.primary, '&:hover': { borderColor: themeColors.primary, bgcolor: `${themeColors.primary}08` } }}
        >
          View
        </Button>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Customer Verification"
        breadcrumb="CRM"
      />

      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1) }}
          searchPlaceholder="Search by name, company, email, mobile, ID..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          extraActions={
            hasFilters && (
              <Button size="small" onClick={clearFilters}
                sx={{ fontSize: '12px', height: 38, textTransform: 'none', color: themeColors.text.secondary, borderRadius: '8px' }}>
                Clear Filters
              </Button>
            )
          }
        />

        {/* Summary chips */}
        {data && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
            {(['Pending', 'In Review', 'Approved', 'Rejected', 'Need More Documents'] as CustomerVerificationStatus[]).map(s => {
              const count = items.filter(r => r.status === s).length
              if (!count) return null
              const cfg = STATUS_CONFIG[s]
              return (
                <Chip key={s} label={`${s}: ${count}`} size="small"
                  sx={{ height: 20, borderRadius: '4px', bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, fontSize: '11px', fontWeight: 600, '& .MuiChip-label': { px: '8px' } }} />
              )
            })}
          </Box>
        )}
      </Box>

      {/* ── Scrollable content ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <DataTable
            columns={columns}
            rows={items}
            isLoading={isLoading}
            selectable={false}
            showRowNumbers={false}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/customer-verification/${row.id}`)}
            emptyIcon={<VerifyIcon />}
            emptyMessage={hasFilters ? 'No verifications match your filters' : 'No customer verifications found'}
            emptyHint={hasFilters ? 'Try adjusting your filters' : 'Customer verification records will appear here'}
            pagination={{
              page,
              pageSize,
              total,
              totalPages,
              itemLabel: 'records',
              onPageChange: setPage,
              onPageSizeChange: (s) => { setPageSize(s); localStorage.setItem('crm_customer_verif_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50],
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
